import { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Button, Dialog } from '../index';

import { RestStepper } from './RestStepper';
import { useTheme, type Theme } from './theme';

// Fail-confirm prompt (AP-0p model). Tapping Fail means the athlete pushed
// one rep past what they could complete. The failed rep is always exactly
// one, so the saved clean count defaults to N-1 (N = reps entered); the
// stepper corrects it EITHER way — down (the last clean rep was also ugly)
// OR up (they actually did a couple more than typed and want to fix it here
// instead of re-editing the row). Confirming writes `reps` = the clean count;
// `fail` is a pure label. Chrome from the shared <Dialog>; number control
// reuses <RestStepper> with a bare-count formatter instead of a new widget.
const MAX_CLEAN_REPS = 99; // practical set-rep ceiling for the ± corrector

type Props = {
  /** Reps entered before the fail (N) — the saved count defaults to N-1. */
  attempted: number;
  /** Save: store the clean rep count, keep the fail. */
  onConfirm: (savedReps: number) => void;
  /** Dismiss (back / backdrop): "not a fail" — clears the fail, keeps reps. */
  onCancel: () => void;
};

export function FailConfirmModal({ attempted, onConfirm, onCancel }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = makeStyles(theme);
  const [value, setValue] = useState(Math.max(0, attempted - 1));

  return (
    <Dialog
      theme={theme}
      visible
      onRequestClose={onCancel}
      dismissOnBackdropPress={false}
      title={t('mobile.logger.failConfirmTitle', { defaultValue: 'Clean reps' })}
    >
      <Text style={styles.prompt}>
        {t('mobile.logger.failConfirmBody', {
          defaultValue: 'Saving {{count}} clean reps. The failed one does not count.',
          count: value,
        })}
      </Text>
      <Text style={styles.hint}>
        {t('mobile.logger.failConfirmHint', {
          defaultValue: 'Adjust if you did more or fewer than that.',
        })}
      </Text>
      <View style={styles.stepper}>
        <RestStepper
          value={value}
          min={0}
          max={MAX_CLEAN_REPS}
          step={1}
          onChange={setValue}
          formatValue={String}
          stepDownLabel={t('mobile.logger.failConfirmStepDown', {
            defaultValue: 'One fewer clean rep',
          })}
          stepUpLabel={t('mobile.logger.failConfirmStepUp', {
            defaultValue: 'One more clean rep',
          })}
          theme={theme}
        />
      </View>
      <View style={styles.actions}>
        <Button
          theme={theme}
          variant="primary"
          label={t('mobile.logger.failConfirmSave', { defaultValue: 'Save' })}
          onPress={() => onConfirm(value)}
        />
      </View>
    </Dialog>
  );
}

const makeStyles = (theme: Theme) =>
  StyleSheet.create({
    prompt: {
      color: theme.palette.ink,
      fontSize: theme.typography.sizes.base,
    },
    hint: {
      color: theme.palette.ink2,
      fontSize: theme.typography.sizes.sm,
      marginTop: theme.spacing.s2,
    },
    stepper: { marginVertical: theme.spacing.s4 },
    actions: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
    },
  });
